/**
 * Leitura do número de risco (código de Kemler), a metade de cima do painel
 * de segurança laranja.
 *
 * Regra do ADR 5.3.2.3 (adotada pela regulamentação brasileira de transporte
 * terrestre de produtos perigosos):
 * - cada algarismo indica um risco;
 * - algarismo repetido indica intensificação daquele risco;
 * - um zero completa o número quando o risco é bem descrito por um algarismo só;
 * - um X na frente: reage perigosamente com água.
 *
 * Alguns números têm significado próprio na tabela do ADR e não se leem
 * algarismo a algarismo (22, 333, 44…). Os do catálogo de ensino e os
 * especiais mais vistos na rodovia estão em `SIGNIFICADOS_ESPECIAIS`; o resto
 * cai na leitura por algarismo.
 */

export const SIGNIFICADO_ALGARISMO: Readonly<Record<string, string>> = {
  '2': 'emissão de gás por pressão ou reação química',
  '3': 'inflamabilidade de líquidos (vapores) e gases, ou líquido sujeito a autoaquecimento',
  '4': 'inflamabilidade de sólidos, ou sólido sujeito a autoaquecimento',
  '5': 'efeito oxidante (comburente)',
  '6': 'toxicidade ou risco de infecção',
  '7': 'radioatividade',
  '8': 'corrosividade',
  '9': 'risco de reação violenta espontânea',
}

const SIGNIFICADOS_ESPECIAIS: Readonly<Record<string, string>> = {
  '20': 'gás asfixiante ou sem risco subsidiário',
  '22': 'gás liquefeito refrigerado, asfixiante',
  '23': 'gás inflamável',
  '265': 'gás tóxico, oxidante (comburente) e corrosivo',
  '268': 'gás tóxico e corrosivo',
  '30': 'líquido inflamável (ponto de fulgor entre 23 °C e 60 °C)',
  '33': 'líquido muito inflamável (ponto de fulgor abaixo de 23 °C)',
  '333': 'líquido pirofórico',
  '44': 'sólido inflamável que, a temperatura elevada, se encontra fundido',
  '80': 'substância corrosiva',
  '90': 'substância perigosa para o meio ambiente; substâncias perigosas diversas',
}

export interface LeituraNumeroRisco {
  numero: string
  valido: boolean
  /** O número tem significado próprio na tabela (não é só a soma dos algarismos). */
  especial: boolean
  reageComAgua: boolean
  /** Uma frase para o aluno, ou o motivo de não ser um número de risco válido. */
  resumo: string
  /** Um item por risco lido, na ordem do painel. */
  riscos: string[]
}

export function lerNumeroRisco(entrada: string): LeituraNumeroRisco {
  const numero = entrada.trim().toUpperCase()
  const invalido = (motivo: string): LeituraNumeroRisco => ({
    numero,
    valido: false,
    especial: false,
    reageComAgua: false,
    resumo: motivo,
    riscos: [],
  })

  if (!/^X?\d{2,3}$/.test(numero)) {
    return invalido('O número de risco tem 2 ou 3 algarismos, opcionalmente precedidos de X.')
  }

  const reageComAgua = numero.startsWith('X')
  const algarismos = reageComAgua ? numero.slice(1) : numero
  const principal = algarismos[0]!
  if (!SIGNIFICADO_ALGARISMO[principal]) {
    return invalido(`O primeiro algarismo indica o risco principal e vai de 2 a 9; "${principal}" não é um risco.`)
  }

  const riscos: string[] = []
  const vistos = new Set<string>()
  for (const [indice, algarismo] of [...algarismos].entries()) {
    if (algarismo === '0') continue
    const significado = SIGNIFICADO_ALGARISMO[algarismo]
    if (!significado) return invalido(`Algarismo "${algarismo}" não tem significado de risco.`)
    if (vistos.has(algarismo)) {
      riscos[riscos.length - 1] = `${riscos[riscos.length - 1]} (intensificado)`
    } else {
      riscos.push(indice === 0 ? `${significado} — risco principal` : significado)
      vistos.add(algarismo)
    }
  }

  const especial = SIGNIFICADOS_ESPECIAIS[algarismos]
  const resumoBase = especial ?? riscos.join('; ')
  return {
    numero,
    valido: true,
    especial: especial !== undefined,
    reageComAgua,
    resumo: reageComAgua ? `${resumoBase}; reage perigosamente com água — não usar água` : resumoBase,
    riscos: reageComAgua ? [...riscos, 'reage perigosamente com água'] : riscos,
  }
}
